import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import { useTheme } from '@mui/material/styles';
import { FeatureFlag, CboRole } from '@cbo/shared-library';
import { useQueryClient } from '@tanstack/react-query';
import routes from '../../constants/routes';
import { ScheduledReport, ScheduleStatus, ScheduledReportsTabId } from '../types';
import { useGetReportsListQuery, useOrganizationUsersQuery } from '../../admin/requests/queries';
import { useDeleteScheduleMutation } from '../../admin/requests/mutations';
import { useAdminRequests } from '../../admin/requests';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { downloadFile } from '../../labor/utilities/laborUtilities';
import ActionsDialog from '../../components/ActionsDialog/ActionsDialog';
import isPermitted from '../../lib/permissions';
import {
  formatDateRange,
  getOccurrenceText,
  getOrganizationUserFullName,
  getSiteNameById,
  isScheduledByCurrentUser,
  MAX_TOOLTIP_ITEMS,
} from '../utils/ViewScheduleReportUtil';
import {
  scheduledReportCardSx,
  cardAction,
  titleBoxSx,
  titleSx,
  descriptionSx,
  iconContainerSx,
  moreOptionsIconButtonSx,
  menuItemSx,
  menuItemLoadingContainerSx,
  dividerSx,
  chipContainerSx,
  scheduledByBoxSx,
  reportCardInnerBoxSx,
  reportCardContentBoxSx,
  reportCardInfoRowSx,
  reportCardIconSx,
  reportCardInfoRowBottomSx,
  storesChipSx,
  recipientsChipSx,
} from '../styles';
import { useUsers } from '../../contexts/userContext';
import { useSites } from '../../contexts/siteContext';
import { useFeatureFlag } from '../../utils/hooks/useFeatureFlag';
import queryKeys from '../../admin/requests/queryKeys';

interface ScheduledReportCardProps {
  report: ScheduledReport;
  currentTabId?: ScheduledReportsTabId;
  hideChipsSection?: boolean;
}
const { EXPIRED, EXPIRING_SOON } = ScheduleStatus;

function ScheduledReportCard({ report, currentTabId, hideChipsSection = false }: Readonly<ScheduledReportCardProps>) {
  const {
    id,
    scheduleName,
    reportName,
    scheduleStatus,
    recurrenceType,
    runTime,
    daysOfWeek,
    dayOfMonth,
    startDate,
    endDate,
    storeIds,
    recipients,
    scheduledBy,
    customFields,
  } = report;

  const { t } = useTranslation();
  const { t: scheduledReportsT } = useTranslation('translation', {
    keyPrefix: 'admin.scheduledReports',
  });

  const isScheduleReportEnabled = useFeatureFlag(FeatureFlag.scheduledReport);

  const theme = useTheme();
  const user = useUsers();
  const queryClient = useQueryClient();

  const { data: organizationUsers } = useOrganizationUsersQuery(true);
  const { data: reportsList } = useGetReportsListQuery(true);
  const { sortedSites } = useSites();
  const { getScheduledReportSignedUrl } = useAdminRequests();
  const { setSnackbarState } = useSnackbar();
  const deleteMutation = useDeleteScheduleMutation();

  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleCardClick = (e: React.MouseEvent) => {
    // Ignore if menu is open or an interactive child handled the event
    if (open || (e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('[role="menu"]')) {
      return;
    }
    history.push(routes.SCHEDULED_REPORT_DETAILS.replace(':scheduleId', id));
  };

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewLatestReport = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();

      if (!customFields?.lastSentReportPrefix) {
        return;
      }

      setIsDownloading(true);

      try {
        const { signedUrl } = await getScheduledReportSignedUrl({
          isScheduleReportExport: true,
          fileNamePrefix: customFields.lastSentReportPrefix as string,
        });

        downloadFile(signedUrl);

        setSnackbarState({
          open: true,
          color: 'success',
          message: scheduledReportsT('actions.downloadSuccess'),
        });

        handleMenuClose();
      } catch (error) {
        setSnackbarState({
          open: true,
          color: 'error',
          message: scheduledReportsT('actions.downloadError'),
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [customFields, getScheduledReportSignedUrl, handleMenuClose, scheduledReportsT, setSnackbarState]
  );

  const handleEditSchedule = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      history.push(routes.EDIT_SCHEDULE_REPORTS.replace(':reportId', id));
      handleMenuClose();
    },
    [history, id, handleMenuClose]
  );

  const handleDeleteSchedule = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setDeleteConfirmationOpen(true);
      handleMenuClose();
    },
    [handleMenuClose]
  );

  const handleCloseDeleteConfirmation = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync({ id });
      // invalidate to fetch latest schedules
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduledReports.list() });

      setSnackbarState({
        open: true,
        color: 'success',
        message: scheduledReportsT('actions.deleteSuccess', { scheduleName }),
      });

      setDeleteConfirmationOpen(false);
    } catch (error) {
      setSnackbarState({
        open: true,
        color: 'error',
        message: scheduledReportsT('actions.deleteError'),
      });
    }
  }, [id, deleteMutation, scheduledReportsT, setSnackbarState, queryClient, scheduleName]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e as unknown as React.MouseEvent);
    }
  };

  // Extract complex rendering logic
  const scheduleText = useMemo(
    () => getOccurrenceText(scheduleStatus, recurrenceType, runTime, daysOfWeek, dayOfMonth, t),
    [scheduleStatus, recurrenceType, runTime, daysOfWeek, dayOfMonth, t]
  );

  const hasStatusIndicator = scheduleStatus === EXPIRED || scheduleStatus === EXPIRING_SOON;
  const statusColor = scheduleStatus === EXPIRED ? theme.palette.error.main : theme.palette.warning.main;
  const statusLabel = hasStatusIndicator ? scheduledReportsT(`status.${scheduleStatus}`) : null;

  const hideScheduledBySection = currentTabId === ScheduledReportsTabId.ScheduledByYou;

  const scheduledByUserName = useMemo(() => {
    if (isScheduledByCurrentUser(scheduledBy, user)) {
      return scheduledReportsT('card.scheduledByYou');
    }
    return getOrganizationUserFullName(scheduledBy, organizationUsers);
  }, [scheduledBy, user, organizationUsers, scheduledReportsT]);

  const numberOfStores = storeIds?.length ?? 0;
  const reportData = useMemo(
    () =>
      reportsList
        ? reportsList.find((reports) => String(reports?.id).toLowerCase() === String(reportName ?? '').toLowerCase())
        : null,
    [reportsList, reportName]
  );
  const categoryLabel = reportData?.title ?? '';

  const storesTooltipContent = useMemo(() => {
    if (!storeIds || !Array.isArray(storeIds) || storeIds.length === 0) return '';
    const displayStores = storeIds.slice(0, MAX_TOOLTIP_ITEMS);
    const remaining = storeIds.length - MAX_TOOLTIP_ITEMS;
    return (
      <Box>
        {displayStores.map((storeId) => (
          <Box key={storeId}>{getSiteNameById(storeId, sortedSites)}</Box>
        ))}
        {remaining > 0 && <Box>{scheduledReportsT('card.more', { count: remaining })}</Box>}
      </Box>
    );
  }, [storeIds, scheduledReportsT, sortedSites]);

  const recipientsTooltipContent = useMemo(() => {
    if (!recipients || !Array.isArray(recipients) || recipients.length === 0) return '';
    const displayRecipients = recipients.slice(0, MAX_TOOLTIP_ITEMS);
    const remaining = recipients.length - MAX_TOOLTIP_ITEMS;
    return (
      <Box>
        {displayRecipients.map((recipient) => (
          <Box key={recipient.username}>{getOrganizationUserFullName(recipient.username, organizationUsers)}</Box>
        ))}
        {remaining > 0 && <Box>{scheduledReportsT('card.more', { count: remaining })}</Box>}
      </Box>
    );
  }, [recipients, organizationUsers, scheduledReportsT]);

  const isAdmin = useMemo(() => isPermitted(user, [CboRole.ADMIN_DASHBOARD_VIEWER]), [user]);
  const isOwner = useMemo(() => isScheduledByCurrentUser(scheduledBy, user), [scheduledBy, user]);
  const canEditOrDelete = isOwner || isAdmin;

  const showViewLatestReport = isScheduleReportEnabled && !!customFields?.lastSentReportPrefix;
  const showMoreOptions = canEditOrDelete || showViewLatestReport;

  return (
    <Card
      sx={scheduledReportCardSx}
      data-testid={`scheduled-report-card-${id}`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <Box sx={cardAction}>
        <Box sx={reportCardInnerBoxSx}>
          <Box sx={titleBoxSx}>
            <Typography sx={titleSx} color='text.primary' data-testid={`report-name-${id}`}>
              {scheduleName}
            </Typography>
            {showMoreOptions && (
              <Box sx={iconContainerSx}>
                <IconButton
                  aria-label={scheduledReportsT('moreActions')}
                  onClick={handleMenuOpen}
                  data-testid={`report-menu-button-${id}`}
                  size='small'
                  sx={moreOptionsIconButtonSx}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={reportCardContentBoxSx}>
          <Box sx={reportCardInfoRowSx}>
            <ScheduleIcon sx={reportCardIconSx} />
            <Typography sx={descriptionSx} color='text.secondary'>
              {scheduleText}
              {hasStatusIndicator && (
                <Typography component='span' sx={descriptionSx} color={statusColor} data-testid={`status-text-${id}`}>
                  {scheduleStatus === EXPIRED ? ' ' : ' - '}
                  {statusLabel}
                </Typography>
              )}
            </Typography>
          </Box>

          <Box sx={reportCardInfoRowBottomSx}>
            <CalendarMonthIcon sx={reportCardIconSx} />
            <Typography sx={descriptionSx} color='text.secondary'>
              {formatDateRange(startDate, endDate ?? '')}
            </Typography>
          </Box>

          {!hideChipsSection && (
            <Box sx={chipContainerSx}>
              <Tooltip title={storesTooltipContent} arrow placement='top'>
                <Chip
                  icon={<StorefrontIcon />}
                  label={`${numberOfStores} ${scheduledReportsT('card.stores')}`}
                  size='small'
                  sx={storesChipSx}
                />
              </Tooltip>
              <Tooltip title={recipientsTooltipContent} arrow placement='top'>
                <Chip icon={<PeopleIcon />} label={recipients.length} size='small' sx={recipientsChipSx} />
              </Tooltip>
              <Chip label={categoryLabel} size='small' />
            </Box>
          )}

          {!hideScheduledBySection && (
            <>
              <Divider sx={dividerSx} />

              <Box sx={scheduledByBoxSx}>
                <Typography sx={descriptionSx} color='text.secondary'>
                  {scheduledReportsT('card.scheduledBy')} {scheduledByUserName}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        data-testid={`report-menu-${id}`}
      >
        {showViewLatestReport && (
          <MenuItem
            onClick={handleViewLatestReport}
            sx={menuItemSx}
            data-testid={`view-latest-${id}`}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Box sx={menuItemLoadingContainerSx}>
                <CircularProgress size={16} />
                {scheduledReportsT('actions.downloading')}
              </Box>
            ) : (
              scheduledReportsT('actions.viewLatest')
            )}
          </MenuItem>
        )}
        {canEditOrDelete && (
          <MenuItem onClick={handleEditSchedule} sx={menuItemSx} data-testid={`edit-schedule-${id}`}>
            {scheduledReportsT('actions.editSchedule')}
          </MenuItem>
        )}
        {canEditOrDelete && (
          <MenuItem onClick={handleDeleteSchedule} sx={menuItemSx} data-testid={`delete-schedule-${id}`}>
            {scheduledReportsT('actions.deleteSchedule')}
          </MenuItem>
        )}
      </Menu>

      <ActionsDialog
        open={deleteConfirmationOpen}
        onClose={handleCloseDeleteConfirmation}
        dataTestId='delete-schedule-confirmation-dialog'
        dialogTitle={scheduledReportsT('deleteConfirmation.title', { scheduleName })}
        dialogContentText={scheduledReportsT('deleteConfirmation.content', {
          reportName,
          occurrence: scheduleText,
        })}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        onActionClick={handleConfirmDelete}
        actionButtonText={t('buttonText.delete')}
        actionButtonColor='error'
        actionButtonVariant='contained'
        loadingActionButton={deleteMutation.isLoading}
        disableActionButton={deleteMutation.isLoading}
      />
    </Card>
  );
}

export default ScheduledReportCard;
